import { useState } from 'react';
import Box from '@mui/material/Box';
import useDuplicateAccountList from './hooks/DuplicateAccountList.hooks';
import SimpleDialog from '../../../../features/common/SimpleDialog';
import SectionAppBar from '../../../components/Section/SectionAppBar';
import CloseButton from '../../../components/Button/CloseButton';
import CancelButton from '../../../components/Button/CancelButton';
import DuplicateAccountRow from './DuplicateAccountRow';
import { DuplicateAccount } from './types/types';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

type DuplicateAccountListProps = {
    onAccountExists: (account: DuplicateAccount) => void;
    onContinueNew: (accountName: string) => void;
};

const DuplicateAccountList = ({ onAccountExists, onContinueNew }: DuplicateAccountListProps) => {
    const { duplicates, hasDuplicates, clearDuplicates, accountName, updateAccountName, open } = useDuplicateAccountList();
    const [error, setError] = useState<string | null>(null); // State to track errors

    const handleAccountExists = (account: DuplicateAccount) => {
        onAccountExists(account);
        clearDuplicates();
    };

    const handleNewAccount = () => {
        if (!accountName.trim()) {
            setError('Account name is required');
            return;
        }
        setError(null); // Clear error if any
        onContinueNew(accountName);
        clearDuplicates();
    };

    const handleCancel = () => {
        clearDuplicates();
        setError(null); // Clear error on cancel
    };

    return (
        <SimpleDialog open={open} onClose={handleNewAccount} padding={0} fullHeight={false}>
            <SectionAppBar
                title={`Possible Duplicate Accounts`}
                subTitle={''}
                buttons={
                    <>
                        <CloseButton variant="outlined" color="secondary" onClick={handleNewAccount}>
                            Continue New
                        </CloseButton>
                        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
                    </>
                }
            />
            <Box sx={{ height: 400, minHeight: 400, maxHeight: 400, overflow: 'auto', padding: 2 }}>
                <TextField
                    value={accountName}
                    onChange={(e) => updateAccountName(e.target.value)}
                    fullWidth
                    autoFocus
                    error={!!error} // Trigger error styles
                    helperText={error} // Display the error message
                />

                {duplicates.map((duplicate, index) => (
                    <DuplicateAccountRow key={index} duplicate={duplicate} onAccountExists={handleAccountExists} />
                ))}
            </Box>
        </SimpleDialog>
    );
};

export default DuplicateAccountList;
